import crypto from 'crypto'
import { v4 as uuidv4 } from 'uuid'
import { redis } from '@/lib/booking-api/redis'
import { captureError } from '@/lib/analytics/sentry'

// Direct access to environment variables
const FACEBOOK_PIXEL_ID = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID
const FACEBOOK_ACCESS_TOKEN =
  process.env.FACEBOOK_ACCESS_TOKEN ||
  'EAAN3LAqRbVYBOycD40s9ZA4M1Ox6Mv5uXk6xU3wBC38tBlO11RvNFSMoeZB1IbxCcaWUNeuq3K2iCEZBoqAtOdg5T0b1U3yFX5IFqMiNzW3pUz6rnv4iAjjjAOZCPsM0EntX7gnabCVVUSFs9JFdBYUebS0ZAivWngKTRdSvVBT9FW1aXdyW4I4PS23yECVdZASgZDZD'

/**
 * Checks if the user has given marketing consent
 * @returns {boolean} True if consent is granted, false otherwise
 */
export function hasMarketingConsent(): boolean {
  // Check if running in browser environment
  if (typeof window === 'undefined') {
    return false // Default to no consent in server environment
  }

  try {
    // First try to read from cookie
    const cookieMatch = document.cookie.match(/cookiePreferences=([^;]+)/)
    if (cookieMatch) {
      const preferences = JSON.parse(decodeURIComponent(cookieMatch[1]))
      return preferences.marketing === true
    }

    // If no cookie, check localStorage
    const localStorageConsent = localStorage.getItem('cookieConsent')
    if (localStorageConsent === 'accepted') {
      // If we only have the basic acceptance flag without details,
      // assume marketing is not accepted
      return false
    }

    // No consent found
    return false
  } catch (error) {
    console.error('[Consent] Error checking marketing consent:', error)
    return false // Be conservative, assume no consent on error
  }
}

/**
 * Generates a unique event ID for deduplication between client and server events
 * Uses Redis counter when available, falls back to UUID
 */
export async function getEventId(): Promise<string> {
  try {
    const id = await redis.incr('fb_event_counter')
    return `event_${id}_${Date.now()}`
  } catch (error) {
    console.error('Failed to generate event ID from Redis:', error)
    captureError(error instanceof Error ? error : new Error('Redis event ID generation failed'), {
      component: 'CAPI',
      function: 'getEventId',
      redisOperation: 'incr',
    })
    return uuidv4()
  }
}

/**
 * Hashes user data for enhanced matching while preserving privacy
 */
export function hashData(data: string): string {
  if (!data) {
    return ''
  }
  return crypto.createHash('sha256').update(data.trim().toLowerCase()).digest('hex')
}

interface UserData {
  em?: string // hashed email
  ph?: string // hashed phone
  fn?: string // hashed first name
  ln?: string // hashed last name
  client_ip_address?: string
  client_user_agent?: string
  ge?: string
  db?: string
  ct?: string
  st?: string
  zp?: string
  country?: string
  // Additional fields for raw data before hashing
  email?: string
  phone?: string
  firstName?: string
  lastName?: string
}

interface EventData {
  [key: string]: any
}

/**
 * Validates the data object against Facebook parameter requirements
 * @returns True if valid, false if invalid parameters detected
 */
function validateFacebookParameters(data: any): { valid: boolean; errors: string[] } {
  const errors: string[] = []

  // Check if data object exists and has required top-level properties
  if (!data || typeof data !== 'object') {
    errors.push('Data object is missing or invalid')
    return { valid: false, errors }
  }

  // Validate data array
  if (!Array.isArray(data.data) || data.data.length === 0) {
    errors.push('Missing or empty data array')
    return { valid: false, errors }
  }

  // Check for required properties in the first event object
  const event = data.data[0]

  if (!event.event_name) {
    errors.push('Missing event_name')
  }

  if (!event.event_time || typeof event.event_time !== 'number') {
    errors.push('Missing or invalid event_time')
  }

  if (!event.event_id) {
    errors.push('Missing event_id')
  }

  if (!event.event_source_url) {
    errors.push('Missing event_source_url')
  }

  // Validate user_data object if it exists
  if (event.user_data) {
    if (typeof event.user_data !== 'object') {
      errors.push('Invalid user_data (not an object)')
    } else {
      // Additional checks for valid user_data fields
      const userDataAllowedKeys = [
        'client_ip_address',
        'client_user_agent',
        'em',
        'ph',
        'fn',
        'ln',
        'ge',
        'db',
        'ct',
        'st',
        'zp',
        'country',
      ]

      Object.keys(event.user_data).forEach((key) => {
        if (!userDataAllowedKeys.includes(key)) {
          errors.push(`Invalid user_data field: ${key}`)
        }

        // Ensure values are strings
        if (typeof event.user_data[key] !== 'string') {
          errors.push(`user_data.${key} must be a string`)
        }
      })
    }
  }

  // Validate custom_data object if it exists
  if (event.custom_data) {
    if (typeof event.custom_data !== 'object') {
      errors.push('Invalid custom_data (not an object)')
    } else {
      // Check each custom_data field to ensure it has allowable types
      Object.entries(event.custom_data).forEach(([key, value]) => {
        if (value !== null && typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
          errors.push(`custom_data.${key} has invalid type: ${typeof value}`)
        }
      })
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}

/**
 * Sends an event to Meta's Conversions API
 * @param eventName Standard Meta event name (e.g., 'Purchase', 'Lead')
 * @param eventData Event parameters
 * @param eventId Unique event ID for deduplication
 * @param userData Optional hashed user data for enhanced matching
 * @param skipConsentCheck Optional parameter to skip consent check (for server-side events only)
 */
export async function sendCAPIEvent(
  eventName: string,
  eventData: EventData,
  eventId: string,
  userData?: UserData,
  skipConsentCheck: boolean = false,
): Promise<boolean> {
  try {
    // Debug logging
    console.log(`[CAPI-DEBUG] Preparing to send ${eventName} event with ID: ${eventId}`)

    // Check for consent unless explicitly skipped (server-side calls)
    if (!skipConsentCheck && typeof window !== 'undefined') {
      const hasConsent = hasMarketingConsent()
      // Additionally check for health data consent
      const hasHealthConsent =
        typeof window !== 'undefined' && localStorage.getItem('cookiePreferences')
          ? JSON.parse(decodeURIComponent(localStorage.getItem('cookiePreferences') || '{}'))?.healthData === true
          : false

      if (!hasConsent) {
        console.log('[CAPI-DEBUG] No marketing consent, skipping event')
        // Without marketing consent, don't send events
        return false
      }

      // For health-related events, require additional health data consent
      if (isHealthRelatedEvent(eventName, eventData) && !hasHealthConsent) {
        console.log('[CAPI-DEBUG] Health-related event but no health consent, skipping event')
        // Without health consent, don't send health events
        return false
      }
    }

    // Check if the required environment variables are available
    if (!FACEBOOK_PIXEL_ID || !FACEBOOK_ACCESS_TOKEN) {
      console.error('[CAPI-DEBUG] Missing Facebook configuration')
      const missingConfigError = new Error('Missing Facebook configuration')
      captureError(missingConfigError, {
        component: 'CAPI',
        function: 'sendCAPIEvent',
        eventName,
        eventId,
        missingPixelId: !FACEBOOK_PIXEL_ID,
        missingAccessToken: !FACEBOOK_ACCESS_TOKEN,
        severity: 'critical',
      })
      return false
    }

    // Validate and enhance user data
    const enhancedUserData: Record<string, any> = {
      client_ip_address: userData?.client_ip_address || '',
      client_user_agent: userData?.client_user_agent || '',
    }

    // Log the raw user data for debugging (truncated for privacy)
    if (userData) {
      console.log('[CAPI-DEBUG] Raw user data fields:', Object.keys(userData).join(', '))
    }

    // Add standard user data fields if available - make sure to hash them
    if (userData?.email) {
      console.log('[CAPI-DEBUG] Hashing email:', userData.email.substring(0, 3) + '...')
      enhancedUserData.em = hashData(userData.email)
    }
    if (userData?.phone) {
      console.log('[CAPI-DEBUG] Hashing phone:', userData.phone.substring(0, 3) + '...')
      enhancedUserData.ph = hashData(userData.phone)
    }
    if (userData?.fn) {
      console.log('[CAPI-DEBUG] Hashing first name:', userData.fn.substring(0, 3) + '...')
      enhancedUserData.fn = hashData(userData.fn)
    } else if (userData?.firstName) {
      console.log('[CAPI-DEBUG] Hashing first name:', userData.firstName.substring(0, 3) + '...')
      enhancedUserData.fn = hashData(userData.firstName)
    }
    if (userData?.ln) {
      console.log('[CAPI-DEBUG] Hashing last name:', userData.ln.substring(0, 3) + '...')
      enhancedUserData.ln = hashData(userData.ln)
    } else if (userData?.lastName) {
      console.log('[CAPI-DEBUG] Hashing last name:', userData.lastName.substring(0, 3) + '...')
      enhancedUserData.ln = hashData(userData.lastName)
    }
    if (userData?.ge) {
      enhancedUserData.ge = hashData(userData.ge)
    }
    if (userData?.db) {
      enhancedUserData.db = hashData(userData.db)
    }
    if (userData?.ct) {
      enhancedUserData.ct = hashData(userData.ct)
    }
    if (userData?.st) {
      enhancedUserData.st = hashData(userData.st)
    }
    if (userData?.zp) {
      enhancedUserData.zp = hashData(userData.zp)
    }
    if (userData?.country) {
      enhancedUserData.country = hashData(userData.country)
    }

    // Remove any non-standard fields that might cause errors
    // These are the only fields we should be sending for user_data
    const validUserDataFields = [
      'client_ip_address',
      'client_user_agent',
      'em',
      'ph',
      'fn',
      'ln',
      'ge',
      'db',
      'ct',
      'st',
      'zp',
      'country',
    ]
    Object.keys(enhancedUserData).forEach((key) => {
      if (!validUserDataFields.includes(key)) {
        console.warn(`[CAPI-DEBUG] Removing invalid user data field: ${key}`)
        delete enhancedUserData[key]
      }
    })

    // If we don't have any user data, log a warning
    if (Object.keys(enhancedUserData).length <= 2) {
      // Only has IP and user agent
      console.warn('[CAPI-DEBUG] No user data provided for CAPI event. This may reduce matching effectiveness.')
    } else {
      console.log('[CAPI-DEBUG] User data fields included:', Object.keys(enhancedUserData).join(', '))
    }

    // Remove user_data from eventData if it exists to avoid duplication
    if (eventData.user_data) {
      console.log('[CAPI-DEBUG] Removing user_data from eventData to avoid duplication')
      const { user_data, ...cleanedEventData } = eventData
      eventData = cleanedEventData
    }

    // CRITICAL: Ensure we always have a valid event_source_url
    // Facebook will block events without this parameter in 54 days
    let eventSourceUrl = eventData.event_source_url || eventData.source_url || ''

    // If no URL is provided, ensure we get one from multiple fallback sources
    if (!eventSourceUrl) {
      // Try to get from window.location first
      if (typeof window !== 'undefined') {
        try {
          eventSourceUrl = window.location.href
          console.log('[CAPI-DEBUG] Using window.location.href for event_source_url:', eventSourceUrl)
        } catch (e) {
          console.error('[CAPI-DEBUG] Failed to get current URL:', e)
          captureError(e instanceof Error ? e : new Error('Failed to get current URL'), {
            component: 'CAPI',
            function: 'sendCAPIEvent',
            eventName,
            eventId,
          })
        }
      }

      // If still no URL, try document.referrer
      if (!eventSourceUrl && typeof document !== 'undefined' && document.referrer) {
        eventSourceUrl = document.referrer
        console.log('[CAPI-DEBUG] Using document.referrer for event_source_url:', eventSourceUrl)
      }

      // If still no URL, try other potential sources like eventData
      if (!eventSourceUrl && eventData.referrer_url) {
        eventSourceUrl = eventData.referrer_url
        console.log('[CAPI-DEBUG] Using eventData.referrer_url for event_source_url:', eventSourceUrl)
      }

      // Last resort fallback - use the website domain with path if available
      if (!eventSourceUrl) {
        // Add path information if available in eventData
        const path = eventData.path || eventData.page_path || ''
        eventSourceUrl = `https://istitutoimage.it${path ? `/${path.replace(/^\//, '')}` : ''}`
        console.log('[CAPI-DEBUG] Using fallback domain for event_source_url:', eventSourceUrl)
      }
    }

    // IMPORTANT: Make sure eventSourceUrl is an absolute URL
    if (eventSourceUrl && !eventSourceUrl.startsWith('http')) {
      eventSourceUrl = `https://istitutoimage.it${eventSourceUrl.startsWith('/') ? '' : '/'}${eventSourceUrl}`
      console.log('[CAPI-DEBUG] Converted to absolute URL:', eventSourceUrl)
    }

    // Ensure event_source_url is not an empty string as it's now required by Meta
    if (!eventSourceUrl) {
      console.error('[CAPI-DEBUG] Failed to set event_source_url, which is now required by Meta')
      const noUrlError = new Error('Failed to set event_source_url, which is now required by Meta')
      captureError(noUrlError, {
        component: 'CAPI',
        function: 'sendCAPIEvent',
        eventName,
        eventId,
        severity: 'critical',
      })
      return false
    }

    // Remove any custom event_source_url from eventData to avoid duplication
    // We'll use our sanitized version only
    if (eventData.event_source_url) {
      delete eventData.event_source_url
    }
    if (eventData.source_url) {
      delete eventData.source_url
    }

    // Sanitize eventData - remove any health-related or sensitive attributes if needed
    const sanitizedEventData = sanitizeEventData(eventData)

    console.log(`[CAPI-DEBUG] Event name: ${eventName}`)
    console.log(`[CAPI-DEBUG] Event ID: ${eventId}`)
    console.log(`[CAPI-DEBUG] Event source URL: ${eventSourceUrl}`)
    console.log(`[CAPI-DEBUG] Event data keys:`, Object.keys(sanitizedEventData).join(', '))

    const data = {
      data: [
        {
          event_name: eventName,
          event_time: Math.floor(Date.now() / 1000),
          event_id: eventId,
          // CRITICAL: Always include event_source_url
          event_source_url: eventSourceUrl,
          action_source: 'website',
          user_data: enhancedUserData,
          custom_data: sanitizedEventData,
          // Flag data as processed with consent
          data_processing_options: ['LDU'],
          data_processing_options_country: 1, // Italy (can adjust based on user country)
          data_processing_options_state: 0, // Not applicable for Italy
        },
      ],
      access_token: FACEBOOK_ACCESS_TOKEN,
      pixel_id: FACEBOOK_PIXEL_ID,
    }

    // Validate parameters before sending
    const validation = validateFacebookParameters(data)
    if (!validation.valid) {
      console.error('[CAPI-DEBUG] Invalid Facebook parameters:', validation.errors)
      captureError(new Error(`Invalid Facebook parameters: ${validation.errors.join(', ')}`), {
        component: 'CAPI',
        function: 'sendCAPIEvent',
        eventName,
        eventId,
        severity: 'warning',
        errors: validation.errors,
      })

      // Try to fix any fixable issues
      if (validation.errors.some((e) => e.includes('custom_data'))) {
        console.log('[CAPI-DEBUG] Attempting to fix custom_data issues')
        // If custom_data has issues, send with minimal custom_data
        data.data[0].custom_data = { value: 0, currency: 'EUR' }
      }

      // Log the modified data
      console.log('[CAPI-DEBUG] Modified Facebook data after validation')
    }

    try {
      console.log(`[CAPI-DEBUG] Sending request to Meta API for ${eventName}`)
      const response = await fetch(`https://graph.facebook.com/v18.0/${FACEBOOK_PIXEL_ID}/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      const result = await response.json()

      if (!response.ok) {
        console.error('[CAPI-DEBUG] CAPI Error:', result)

        // Log more detailed error information
        if (result.error) {
          console.error('[CAPI-DEBUG] Error details:')
          console.error('- Message:', result.error.message)
          console.error('- Type:', result.error.type)
          console.error('- Code:', result.error.code)
          console.error('- Subcode:', result.error.error_subcode)

          if (result.error.error_user_title) {
            console.error('- User title:', result.error.error_user_title)
          }

          if (result.error.error_user_msg) {
            console.error('- User message:', result.error.error_user_msg)
          }

          // Report to Sentry with detailed context
          captureError(new Error(`Meta CAPI Error: ${result.error.message}`), {
            component: 'CAPI',
            function: 'sendCAPIEvent',
            eventName,
            eventId,
            errorType: result.error.type,
            errorCode: result.error.code,
            errorSubcode: result.error.error_subcode,
            errorMessage: result.error.message,
            errorUserTitle: result.error.error_user_title,
            errorUserMsg: result.error.error_user_msg,
            httpStatus: response.status,
            severity: 'error',
          })
        }

        return false
      }

      console.log('[CAPI-DEBUG] CAPI Success:', result)
      return true
    } catch (fetchError) {
      console.error('[CAPI-DEBUG] CAPI Fetch Error:', fetchError)
      captureError(fetchError instanceof Error ? fetchError : new Error('CAPI Fetch Error'), {
        component: 'CAPI',
        function: 'sendCAPIEvent',
        eventName,
        eventId,
        endpoint: `https://graph.facebook.com/v18.0/${FACEBOOK_PIXEL_ID}/events`,
        severity: 'error',
      })
      return false
    }
  } catch (error) {
    console.error('[CAPI-DEBUG] CAPI Request Error:', error)
    captureError(error instanceof Error ? error : new Error('CAPI Request Error'), {
      component: 'CAPI',
      function: 'sendCAPIEvent',
      eventName,
      eventId,
      severity: 'error',
    })
    return false
  }
}

/**
 * Checks if an event is health-related based on content and context
 */
function isHealthRelatedEvent(eventName: string, eventData: EventData): boolean {
  // Check event name for health-related terms
  const healthEventNames = ['ViewContent', 'Lead', 'BookTreatment', 'BookLastMinute', 'BookConsultation']

  // Certain events are always considered health-related in our context
  if (healthEventNames.includes(eventName)) {
    return true
  }

  // Check if event data explicitly marks it as health-related
  if (
    eventData.isHealthRelated === true ||
    eventData.health_related === true ||
    eventData.options?.isHealthRelated === true
  ) {
    return true
  }

  return false
}

/**
 * Sanitizes event data to remove sensitive health information
 */
function sanitizeEventData(eventData: EventData): EventData {
  // Create a copy to avoid modifying the original
  const sanitized = { ...eventData }

  // List of keys that could contain sensitive health data
  const sensitiveKeys = [
    'condition',
    'disease',
    'diagnosis',
    'treatment',
    'medical',
    'health',
    'patient',
    'symptom',
    'therapy',
    'medication',
    'procedure',
  ]

  // Remove any keys that might contain sensitive health data
  Object.keys(sanitized).forEach((key) => {
    const lowerKey = key.toLowerCase()
    if (sensitiveKeys.some((sk) => lowerKey.includes(sk))) {
      delete sanitized[key]
    }
  })

  // Special handling for content_name and content_type to make them more generic
  if (
    sanitized.content_type &&
    (sanitized.content_type.toLowerCase().includes('treatment') ||
      sanitized.content_type.toLowerCase().includes('medical'))
  ) {
    sanitized.content_type = 'service'
  }

  // For descriptions or other text fields that might contain sensitive info
  if (sanitized.content_name) {
    sanitized.content_name = 'Service Inquiry'
  }

  // Clean content_id to remove any query parameters that might cause issues
  if (sanitized.content_id && typeof sanitized.content_id === 'string') {
    // Remove query parameters from content_id
    sanitized.content_id = sanitized.content_id.split('?')[0]

    // If content_id contains URL-encoded characters, simplify it
    if (sanitized.content_id.includes('%')) {
      sanitized.content_id = 'page_content'
    }

    // Ensure content_id is not too long (Meta has length limits)
    if (sanitized.content_id.length > 100) {
      sanitized.content_id = sanitized.content_id.substring(0, 100)
    }
  }

  // Additional validation for Facebook-expected values
  // Convert any null/undefined values to empty strings instead
  Object.keys(sanitized).forEach((key) => {
    if (sanitized[key] === null || sanitized[key] === undefined) {
      sanitized[key] = ''
    }

    // Ensure values are strings, numbers, or booleans (arrays and objects aren't allowed in custom_data)
    if (typeof sanitized[key] === 'object') {
      // For objects/arrays, stringify or delete
      if (Array.isArray(sanitized[key])) {
        // If it's a simple array of primitives, join as comma-separated string
        if (sanitized[key].every((item) => typeof item !== 'object')) {
          sanitized[key] = sanitized[key].join(',')
        } else {
          delete sanitized[key] // Remove complex arrays
        }
      } else {
        // Remove nested objects entirely
        delete sanitized[key]
      }
    }
  })

  // Remove the health-related flags after checking them
  delete sanitized.isHealthRelated
  delete sanitized.health_related
  delete sanitized.is_health_related
  delete sanitized.is_health
  if (sanitized.options) {
    delete sanitized.options.isHealthRelated
  }

  return sanitized
}
